import { useMemo, useState } from 'react'

import styles from './ManageDevicesModal.module.scss'
import { withLocalize } from 'react-localize-redux'
import { Typography } from '@material-ui/core'
import Button from 'common/components/Button/Button'
import { browserHistory } from 'react-router'
import * as Routes from 'constants/Routes'

import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined'
import PropTypes from 'prop-types'
import { DeviceSelector } from 'modules/modals/NewModals/Components/Molecules/DevicesSelector/DevicesSelector'
import { InfoPopover } from 'common/components/InfoPopover/InfoPopover'
import { Tooltip } from '@mui/material'

const ManageDevicesModal = props => {
  const { translate, userSystems, systems, projects, isSubmitting, onSubmit } = props

  const [filterExpression, setFilterExpression] = useState('')
  const [selectedProjectCheckboxIds, setSelectedProjectCheckboxIds] = useState([])
  const [selectedUserCheckboxIds, setSelectedUserCheckboxIds] = useState([])
  const [projectSystems, setProjectSystems] = useState(systems)

  // Original deviceId -> group map persisted on the project. Source of truth for
  // restoring a device's group when it is removed and re-added during this session.
  const originalGroupById = useMemo(
    () => ({ ...(projects?.toJS?.().Meta?.DeviceIds || {}) }),
    [projects]
  )

  const handleFilterExpressionChange = event => {
    setFilterExpression(event.target.value)
  }

  const redirectToDevices = () => {
    browserHistory.push(Routes.DEVICES)
    props.hideModal()
  }

  const handleChangeProjectCheckbox = selectedCheckboxIds => {
    setSelectedProjectCheckboxIds(selectedCheckboxIds)
  }
  const handleChangeUserDevice = selectedCheckboxIds => {
    setSelectedUserCheckboxIds(selectedCheckboxIds)
  }

  const onClickRemoveDevices = () => {
    setProjectSystems(prevSystems =>
      prevSystems.filter(system => !selectedProjectCheckboxIds.includes(system._id))
    )
    setSelectedProjectCheckboxIds([])
  }

  const onClickAddDevices = () => {
    const devicesToAdd = userSystems
      .filter(system => selectedUserCheckboxIds.includes(system._id))
      // Restore the group the device had on this project (if any) so it renders
      // back in its original bucket instead of falling into "No group".
      .map(system => ({
        ...system,
        group: originalGroupById[system._id] ?? system.group ?? ''
      }))
    setProjectSystems(prevSystems => [...prevSystems, ...devicesToAdd])
    setSelectedUserCheckboxIds([])
  }

  let projectSystemsJS =
    filterExpression !== ''
      ? projectSystems.filter(s => s.name.toLowerCase().includes(filterExpression.toLowerCase()))
      : projectSystems

  let userSystemsJS = userSystems.filter(s => {
    const isInProject = projectSystemsJS.some(p => p._id === s._id)
    if (filterExpression !== '') {
      return !isInProject && s.name.toLowerCase().includes(filterExpression.toLowerCase())
    }
    return !isInProject
  })

  const handleClickAddDevices = () => {
    // Build the deviceId -> group payload from each device's current group, which
    // already reflects the original group for re-added devices. Fall back to the
    // persisted snapshot, then to "" (no group) for genuinely new devices.
    const groups = projectSystems.reduce((acc, system) => {
      acc[system._id] = system.group ?? originalGroupById[system._id] ?? ''
      return acc
    }, {})
    onSubmit({ devices: groups })
  }
  const onClickCancel = () => {
    props.hideModal()
  }
  return (
    <div className={styles.modalContainer}>
      <Typography className={styles.header}>{translate('manageDevices')}</Typography>
      <div className={styles.modal}>
        <form
          noValidate
          onKeyDown={ev => {
            if (ev.key === 'Enter') {
              ev.preventDefault()
            }
          }}
        >
          <Typography className={styles.subTitle}>{translate('selectDevicesTitle')}</Typography>
          <div className={styles.searchDeviceInput}>
            <SearchOutlinedIcon />
            <input
              type='text'
              id='searchDevice'
              name='searchDevice'
              placeholder={translate('searchDevice')}
              onChange={handleFilterExpressionChange}
              value={filterExpression}
            />
          </div>

          <div
            className={styles.devicesList}
            id='devicesList'
          >
            <div className={styles.newGroupContainer}>
              <DeviceSelector
                systems={projectSystemsJS}
                selectedCheckboxIds={selectedProjectCheckboxIds}
                setSelectedCheckboxIds={handleChangeProjectCheckbox}
                translate={translate}
                userWritting={filterExpression.length > 0}
                checkboxColor='#f53152'
                grouping_mode='type'
                selectAllLabel={translate('devicesOnProject')}
                selectAllInfoButton={
                  <InfoPopover
                    title={translate('devicesOnProject')}
                    message={translate('devicesOnProjectInfo')}
                    linkAction={redirectToDevices}
                    linkText={translate('myDevices')}
                  />
                }
              />
            </div>
            {projectSystemsJS.length > 0 && (
              <div className={styles.listButtonWrapper}>
                <Button
                  type='light-delete'
                  label={translate('removeDevices')}
                  onClick={onClickRemoveDevices}
                />
              </div>
            )}
          </div>

          <div
            className={styles.devicesList}
            id='devicesList'
          >
            <div className={styles.newGroupContainer}>
              <DeviceSelector
                systems={userSystemsJS}
                selectedCheckboxIds={selectedUserCheckboxIds}
                setSelectedCheckboxIds={handleChangeUserDevice}
                translate={translate}
                userWritting={filterExpression.length > 0}
                grouping_mode='type'
                selectAllLabel={translate('manageDevicesForm.selectDevices')}
              />
            </div>
            {userSystemsJS.length > 0 && (
              <div className={styles.listButtonWrapper}>
                <Button
                  type='add'
                  label={translate('assignDevices')}
                  onClick={onClickAddDevices}
                />
              </div>
            )}
          </div>
          <div className={styles.buttonWrapper}>
            {selectedUserCheckboxIds.length > 0 || selectedProjectCheckboxIds.length > 0 ? (
              <Tooltip
                placement='top-start'
                enterDelay={100}
                leaveDelay={200}
                title={translate('saveSelectedAddRemove')}
              >
                <div>
                  <Button
                    disabled
                    type='next'
                    label={translate('save')}
                    onClick={handleClickAddDevices}
                  />
                </div>
              </Tooltip>
            ) : (
              <Button
                isLoading={isSubmitting}
                type='next'
                label={translate('save')}
                onClick={handleClickAddDevices}
              />
            )}
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              type='cancel'
              disabled={isSubmitting}
              onClick={onClickCancel}
              label={translate('cancel')}
            />
          </div>
        </form>
      </div>
    </div>
  )
}
ManageDevicesModal.propTypes = {
  translate: PropTypes.func.isRequired
}

export default withLocalize(ManageDevicesModal)
